import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../services';
import { Tag } from '../../Tags/types';
import { ExpandedPropertySet, PropertySet, propertySetDefaults } from './types';
import { AxiosResponse } from 'axios';
import { Property } from '../Properties/types.ts';

export const usePropertySets = (propertySetsIds: number[] = [], loadAll: boolean = true) => useQuery<PropertySet[]>({
  queryKey: ['property-sets'],
  queryFn: () => api.get(`property-sets`).then((res) => res.data),
  select: (data) => {
    if (propertySetsIds.length === 0) {
      return loadAll ? data : [];
    }
    const propertySetMap = new Map(data.map(propertySet => [propertySet.id, propertySet]));
    return propertySetsIds
      .map(id => propertySetMap.get(id))
      .filter((propertySet): propertySet is PropertySet => propertySet !== undefined);
  },
});

export const usePropertySet = (propertySetId: string | undefined, isCreate: boolean) => useQuery<PropertySet>({
  queryKey: ['propertySet', propertySetId],
  queryFn: async (): Promise<PropertySet> => {
    if (isCreate) {
      return Promise.resolve(propertySetDefaults);
    } else {
      const res = await api.get(`property-sets/${propertySetId}`);
      return res.data;
    }
  },
});

export const useUpdatePropertySet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (propertySet: PropertySet) => api.put(`property-sets/${propertySet.id}`, propertySet),
    onSuccess: (response: AxiosResponse<PropertySet>) => {
      void queryClient.invalidateQueries({ queryKey: ['property-sets'] });
      void queryClient.invalidateQueries({ queryKey: ['propertySet', response.data.id] });
      queryClient.setQueryData(['propertySet', `${response.data.id}`], response.data);
    },
  });
}

export const useCreatePropertySet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (propertySet: PropertySet) => api.post(`property-sets`, propertySet),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['property-sets'] });
    },
  });
}

export const usePropertySetTags = () => useQuery<Tag[]>({
  queryKey: ['propertySetTags'],
  queryFn: () => api.get(`tags/type/property-set`).then((res) => res.data),
});

export const useExpandedPropertySet = (propertySetId: number) => useQuery<ExpandedPropertySet>({
  queryKey: ['expandedPropertySet', propertySetId],
  queryFn: async (): Promise<ExpandedPropertySet> => {
      const res = await api.get(`property-sets/${propertySetId}/expand`);
      return res.data;
  },
  cacheTime: 0,
});

export interface PropertySetLocation {
  entityId: number;
  entityName: string;
  entityType: string;
  offerType: string | null;
}

export interface PropertySetLocationsResponse {
  entityLocations: PropertySetLocation[];
  referencingPropertySetIds: number[];
}

export const usePropertySetLocations = (propertySetId: number | undefined, enabled: boolean = true) =>
  useQuery<PropertySetLocationsResponse>({
    queryKey: ['propertySetLocations', propertySetId],
    queryFn: async (): Promise<PropertySetLocationsResponse> => {
      const res = await api.get(`property-sets/${propertySetId}/locations`);
      return res.data;
    },
    enabled: !!propertySetId && enabled,
    cacheTime: 0,
  });

export const useImportPropertySet = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expandedPropertySet: ExpandedPropertySet) => api.post('property-sets/import', expandedPropertySet),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['property-sets'] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}
